/**
 * Auth API client — autenticazione Phase 5 (IMPL-P5-003).
 *
 * Typed `fetch` wrappers for the auth endpoints exposed by the API:
 *  - Custom routes mounted under `/api/auth/custom/*` (Phase 4).
 *  - Better Auth catch-all under `/api/auth/*` (only `sign-out` is used here).
 *
 * Conventions:
 *  - Every call sends `credentials: 'include'` so the httpOnly Better Auth
 *    session cookie travels with the request.
 *  - Errors thrown by these helpers carry a `status: number` and `code: string`
 *    fields (best effort from the API JSON envelope) so callers can branch on
 *    HTTP status (e.g. 409, 401, 429) and map server codes to Italian copy
 *    via {@link serverErrorToItalian}.
 *  - Inputs are typed via the shared Zod-inferred types from `@app/shared`,
 *    keeping the client and the API boundary in lockstep.
 */
import type {
  SignupInput,
  LoginInput,
  ForgotPasswordInput,
  ResetPasswordInput,
} from '@app/shared';

const API = '/api/auth';
const CUSTOM = `${API}/custom`;

export type Role = 'CLIENT' | 'OPERATOR' | 'ADMIN';

export interface SessionUser {
  id: string;
  email: string;
  role: Role;
  tipologia: string | null;
  emailVerifiedAt: string | null;
}

export interface MeResponse {
  user: SessionUser | null;
}

export interface LoginResponse {
  user: SessionUser;
}

export interface AuthClientError extends Error {
  status: number;
  code: string;
}

/**
 * POST helper: serializes JSON body, includes credentials, and on non-2xx
 * surfaces a typed Error with `status` + `code` set from the API envelope.
 */
async function postJson<T>(url: string, body: unknown): Promise<T> {
  const res = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body ?? {}),
  });

  if (!res.ok) {
    const fallback = { code: 'UNKNOWN', message: 'Errore sconosciuto' };
    const payload = (await res.json().catch(() => fallback)) as {
      code?: string;
      message?: string;
    };
    const err = new Error(payload.message ?? 'Error') as AuthClientError;
    err.status = res.status;
    err.code = payload.code ?? 'UNKNOWN';
    throw err;
  }

  // Some endpoints (logout, logout-all) may return an empty body — guard.
  const text = await res.text();
  if (!text) return {} as T;
  return JSON.parse(text) as T;
}

export const authClient = {
  signup: (input: SignupInput) =>
    postJson<{ ok: true; requiresVerification: boolean }>(`${CUSTOM}/signup`, input),

  login: (input: LoginInput) => postJson<LoginResponse>(`${CUSTOM}/login`, input),

  /**
   * Better Auth's built-in sign-out endpoint clears the session cookie.
   * Custom logout-all (below) is for the multi-device flow.
   */
  logout: () => postJson<{ ok: true }>(`${API}/sign-out`, {}),

  logoutAll: () => postJson<{ ok: true }>(`${CUSTOM}/logout-all`, {}),

  forgotPassword: (input: ForgotPasswordInput) =>
    postJson<{ ok: true }>(`${CUSTOM}/forgot-password`, input),

  resetPassword: (input: ResetPasswordInput) =>
    postJson<{ ok: true }>(`${CUSTOM}/reset-password`, input),

  verifyEmail: (token: string) =>
    postJson<{ ok: true }>(`${CUSTOM}/verify-email`, { token }),

  resendVerification: (email: string) =>
    postJson<{ ok: true }>(`${CUSTOM}/resend-verification`, { email }),

  /**
   * GET /api/auth/custom/me — returns the current session or `{ user: null }`
   * on 401 (anonymous). Other non-2xx responses throw.
   */
  getMe: async (): Promise<MeResponse> => {
    const res = await fetch(`${CUSTOM}/me`, { credentials: 'include' });
    if (res.status === 401) return { user: null };
    if (!res.ok) {
      const err = new Error('Failed to fetch session') as AuthClientError;
      err.status = res.status;
      err.code = 'ME_FAILED';
      throw err;
    }
    return (await res.json()) as MeResponse;
  },
};

/**
 * Type guard for caller code that wants to inspect status/code on a thrown
 * error without using `instanceof` against a runtime-mutated Error subclass.
 */
export function isAuthClientError(err: unknown): err is AuthClientError {
  return (
    err instanceof Error &&
    typeof (err as AuthClientError).status === 'number' &&
    typeof (err as AuthClientError).code === 'string'
  );
}
